<script setup>
// AGENTS-LINK-01 (L-11): a entrada da equipe no link de teste, do lado do painel.
//
// "Sou da equipe", na página `/t`, traz a pessoa até aqui com o token do link no fragmento. Logado
// e com as travas de MFA de sempre, o painel pede o passe à API e volta para a página do link com
// ele — a página nunca lê a sessão do painel. O token sai do endereço assim que é lido: o fragmento
// não vai ao servidor, mas iria para o histórico e para o rastreio de erros do painel.
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import testLinkPassesAPI from 'dashboard/api/testLinkPasses';

const { t } = useI18n();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');

const lerToken = () => {
  const bruto = window.location.hash.replace(/^#/, '');
  try {
    return decodeURIComponent(bruto);
  } catch (e) {
    return bruto;
  }
};

const token = lerToken();
if (token) {
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, '', `${pathname}${search}`);
}

// carregando | nao_membro | link_sem_membros | link_inativo | mfa | erro
const estado = ref('carregando');

// O `error` da API vira o estado da tela. `mfa_setup_required` vem das travas de MFA — a da
// plataforma e a da conta do link.
const ESTADO_DO_ERRO = {
  nao_membro: 'nao_membro',
  link_sem_membros: 'link_sem_membros',
  link_inativo: 'link_inativo',
  mfa_setup_required: 'mfa',
};

const mensagem = computed(
  () =>
    ({
      nao_membro: {
        titulo: t('POWER_AGENTS.TEST_LINK_TEAM.NOT_MEMBER_TITLE'),
        texto: t('POWER_AGENTS.TEST_LINK_TEAM.NOT_MEMBER_TEXT'),
      },
      link_sem_membros: {
        titulo: t('POWER_AGENTS.TEST_LINK_TEAM.NO_MEMBERS_TITLE'),
        texto: t('POWER_AGENTS.TEST_LINK_TEAM.NO_MEMBERS_TEXT'),
      },
      link_inativo: {
        titulo: t('POWER_AGENTS.TEST_LINK_TEAM.INACTIVE_TITLE'),
        texto: t('POWER_AGENTS.TEST_LINK_TEAM.INACTIVE_TEXT'),
      },
      mfa: {
        titulo: t('POWER_AGENTS.TEST_LINK_TEAM.MFA_TITLE'),
        texto: t('POWER_AGENTS.TEST_LINK_TEAM.MFA_TEXT'),
      },
      erro: {
        titulo: t('POWER_AGENTS.TEST_LINK_TEAM.ERROR_TITLE'),
        texto: t('POWER_AGENTS.TEST_LINK_TEAM.ERROR_TEXT'),
      },
    })[estado.value]
);

const pedirPasse = async () => {
  if (!token) {
    estado.value = 'link_inativo';
    return;
  }
  estado.value = 'carregando';
  try {
    const { url } = await testLinkPassesAPI.pedir(token);
    // `replace`: voltar da página do link não deve cair aqui de novo, pedindo outro passe.
    window.location.replace(url);
  } catch (e) {
    estado.value = ESTADO_DO_ERRO[e?.response?.data?.error] || 'erro';
  }
};

const configurarMfa = () =>
  router.push({
    name: 'profile_settings_mfa',
    params: { accountId: currentUser.value.account_id },
  });

onMounted(pedirPasse);
</script>

<template>
  <div
    class="flex items-center justify-center w-full h-full min-h-screen p-6 bg-n-background"
  >
    <div
      class="flex flex-col w-full max-w-md gap-4 p-8 border rounded-2xl border-n-weak bg-n-solid-1"
    >
      <div
        v-if="estado === 'carregando'"
        class="flex flex-col items-center gap-3 py-6"
        role="status"
      >
        <Spinner />
        <span class="text-sm text-n-slate-11">
          {{ t('POWER_AGENTS.TEST_LINK_TEAM.LOADING') }}
        </span>
      </div>

      <template v-else>
        <h1 class="mb-0 text-lg font-medium text-n-slate-12">
          {{ mensagem.titulo }}
        </h1>
        <p class="mb-0 text-sm text-n-slate-11">{{ mensagem.texto }}</p>
        <div
          v-if="estado === 'mfa' || estado === 'erro'"
          class="flex justify-end"
        >
          <Button
            v-if="estado === 'mfa'"
            :label="t('POWER_AGENTS.TEST_LINK_TEAM.MFA_ACTION')"
            @click="configurarMfa"
          />
          <Button
            v-else
            :label="t('POWER_AGENTS.TEST_LINK_TEAM.RETRY')"
            slate
            faded
            @click="pedirPasse"
          />
        </div>
      </template>
    </div>
  </div>
</template>
